import { eachDayOfInterval } from 'date-fns/eachDayOfInterval'
import { eachYearOfInterval } from 'date-fns/eachYearOfInterval'
import { format } from 'date-fns/format'
import { getDate } from 'date-fns/getDate'
import { getMonth } from 'date-fns/getMonth'
import { getYear } from 'date-fns/getYear'
import { setDate } from 'date-fns/setDate'
import { setMonth } from 'date-fns/setMonth'
import { setYear } from 'date-fns/setYear'
import { useEffect, useMemo, useState } from 'react'

import { cn } from '@/utils/tw-helpers'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select'

interface Props {
  defaultValue?: Date
  error?: boolean
  isSmall?: boolean
  onValueChange: (datetime: string) => void
}

export const BirthdatePicker = ({
  defaultValue,
  isSmall,
  onValueChange,
}: Props): JSX.Element => {
  const [yearValue, setYearValue] = useState<string>(
    defaultValue ? '' + getYear(defaultValue) : ''
  )
  const [monthValue, setMonthValue] = useState<string>(
    defaultValue ? '' + getMonth(defaultValue) : ''
  )
  const [dateValue, setDateValue] = useState<string>(
    defaultValue ? '' + getDate(defaultValue) : ''
  )

  const triggerCls = cn(
    isSmall ? 'w-[120px]' : 'w-[145px]',
    '[&_.icon-down]:w-[30px] [&_.icon-down]:text-primary-100 [&_.icon-up]:w-[30px] [&_.icon-up]:text-primary-100'
  )

  const years = useMemo(
    () =>
      eachYearOfInterval({
        start: new Date(1970, 0, 1),
        end: new Date(),
      }).reverse(),
    []
  )

  const days = useMemo(
    () =>
      eachDaysOfMonth(
        monthValue ? Number(monthValue) : getMonth(new Date()),
        yearValue ? Number(yearValue) : getYear(new Date())
      ),
    [monthValue, yearValue]
  )

  const onYearChange = (year: string) => {
    setYearValue(year)

    if (dateValue) {
      setDateValue(
        `${getDate(getValidDatetime({ year, month: monthValue, day: dateValue }))}`
      )
    }
  }

  const onMonthChange = (month: string) => {
    setMonthValue(month)
    if (dateValue) {
      setDateValue(
        `${getDate(getValidDatetime({ year: yearValue, month, day: dateValue }))}`
      )
    }
  }

  const onDateChange = (day: string) => {
    setDateValue(day)
  }

  const getValidDatetime = ({
    year,
    month,
    day,
  }: {
    year?: string
    month?: string
    day?: string
  }) => {
    let datetime = new Date()

    if (year) {
      datetime = setYear(datetime, Number(year))
    }
    if (month) {
      datetime = setMonth(datetime, Number(month))
    }
    if (day) {
      datetime = setDate(datetime, Number(day))
    }

    return datetime
  }

  useEffect(() => {
    if (!yearValue || !monthValue || !dateValue) return

    onValueChange(
      format(
        new Date(Number(yearValue), Number(monthValue), Number(dateValue)),
        'yyyy/MM/dd'
      )
    )
  }, [yearValue, monthValue, dateValue, onValueChange])

  return (
    <div className="flex gap-x-6">
      <Select name="year" value={yearValue} onValueChange={onYearChange}>
        <SelectTrigger className={triggerCls}>
          <SelectValue placeholder="年" />
        </SelectTrigger>

        <SelectContent>
          <SelectGroup>
            {years.map((year, index) => (
              <SelectItem key={index} value={`${year.getFullYear()}`}>
                {year.getFullYear()}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      <Select name="month" value={monthValue} onValueChange={onMonthChange}>
        <SelectTrigger className={triggerCls}>
          <SelectValue placeholder="月" />
        </SelectTrigger>

        <SelectContent>
          <SelectGroup>
            {[...Array(12)].map((_, index) => (
              <SelectItem key={index} value={`${index}`}>
                {index + 1}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>

      <Select name="date" value={dateValue} onValueChange={onDateChange}>
        <SelectTrigger className={triggerCls}>
          <SelectValue placeholder="日" />
        </SelectTrigger>

        <SelectContent>
          <SelectGroup>
            {days.map((d) => (
              <SelectItem key={d} value={`${d}`}>
                {d}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
}

const eachDaysOfMonth = (month: number, year: number) => {
  return eachDayOfInterval({
    start: new Date(year, month, 1),
    end: new Date(year, month + 1, 0),
  }).map((date) => getDate(date))
}
